// initial state
import docendo from '../../docendo'
import eventbus from '../../eventbus'
import Vue from 'vue'
import moment from 'moment'

const state = {
  calendarId: null,
  calendar: null,
  date: moment(),
  subjects: [],
  isSubjectsLoading: true,
  isSubjectsLoadingTimer: null,
  events: [],
  isEventsLoading: false,
  isEventsLoadingTimer: null,
  activeEventId: null,
  isDeletingEvent: false,
  editSubject: null,
  availabilityEvents: [],
  isCmdKeyDown: false,
  selectedEventIds: []
}

const getters = {
  calendarStartDate: (state, getters, rootState, rootGetters) => {
    const firstDay = window.SCHOOL && window.SCHOOL.first_day_of_week_num ? window.SCHOOL.first_day_of_week_num : 1
    return moment(state.date).isoWeekday(firstDay).format('YYYY-MM-DD 00:00')
  },
  calendarEndDate: state => {
    const firstDay = window.SCHOOL && window.SCHOOL.first_day_of_week_num ? window.SCHOOL.first_day_of_week_num : 1
    let lastDay = firstDay + 6
    return moment(state.date).isoWeekday(lastDay).endOf('day')
  },
  activeCalendar: (state, getters, rootState, rootGetters) => {
    return rootGetters.allCalendarsInHash[state.calendarId] ? rootGetters.allCalendarsInHash[state.calendarId] : state.calendar
  },
  isUserCanEditCalendar: (state, getters, rootState, rootGetters) => {
    if (rootGetters.isPermissionsActivatedForSchool) {
      return rootGetters.userPermissions.indexOf('can_do_most_stuff') > -1
    } else {
      return true
    }
  },
  // Used for WeekPlan
  activeFullCalendarEvents: (state, getters) => {
    return state.events.map(event => {
      if (event.event_type === 'presence') {
        Vue.set(event, 'display', 'background')
        Vue.set(event, 'rendering', 'background')
        Vue.set(event, 'className', 'fc-bgevent__presence')
        return event
      }
      let hasNotes = event.description.replace(/<(?:.|\n)*?>/gm, '') || event.notes_for_students.replace(/<(?:.|\n)*?>/gm, '') || event.teacher_notes.replace(/<(?:.|\n)*?>/gm, '') || event.learning_goal.replace(/<(?:.|\n)*?>/gm, '')
      Vue.set(event, 'has_notes', hasNotes)
      return event
    })
  },
  activeFullCalendarEventsInHash: (state, getters) => getters.activeFullCalendarEvents.reduce((obj, item) => {
    obj[item.event_id] = item
    return obj
  }, {}),
  activeCalendarEvent: (state) => {
    let idx = state.activeEventId ? state.events.findIndex(event => event.event_id === state.activeEventId) : -1
    return idx > -1 ? state.events[idx] : null
  },
  // Multi-select (masseshandling) – vis checkbox mens cmd/ctrl holdes nede, eller når noget er valgt
  isMultiSelectActive: (state) => state.isCmdKeyDown || state.selectedEventIds.length > 0,
  selectedCalendarEvents: (state) => state.events.filter(event => state.selectedEventIds.indexOf(event.event_id) > -1)
}

const mutations = {
  setActiveCalendarId (state, calendarId) {
    state.calendarId = calendarId
  },
  setActiveCalendarDate (state, date) {
    state.date = date
  },
  addCalendarSubject (state, subject) {
    state.subjects.push(subject)
  },
  setEditSubject (state, subject) {
    state.editSubject = subject
  },
  updateCalendarEvents (state, updatedLessons) {
    updatedLessons.updates.forEach(updatedLessonObj => {
      // Only update start- and end_time to make calendar reactive
      if (updatedLessonObj.start_time && updatedLessonObj.end_time) {
        const idx = state.events.findIndex(event => event.event_id === updatedLessonObj.event_id)
        let currentLesson = state.events[idx]
        // Hack because received lesson object is different structure from original, so only take prop
        currentLesson.start = updatedLessonObj.start_time
        currentLesson.end = updatedLessonObj.end_time
        Vue.set(state.events, idx, currentLesson)
      }
    })
  },
  updateEventId (state, lesson) {
    let idx = state.events.findIndex(event => event.event_id === lesson.event_id)
    if (idx < 0) {
      let idxEventWithNoId = state.events.findIndex(event => event.event_id === 'd_temp_id')
      state.events[idxEventWithNoId].event_id = lesson.event_id
    }
  },
  addActiveCalendarEvent (state, lesson) {
    state.events.push(lesson)
  },
  deleteActiveCalendarEvent (state, lesson) {
    let idx = state.events.findIndex(event => event.event_id === lesson.event_id)
    if (idx > -1) {
      state.events.splice(idx, 1)
    }
  },
  updateActiveCalendarEventNote (state, updateObj) {
    let idx = state.events.findIndex(event => event.event_id === updateObj.payload.id)
    let updatedLesson = state.events[idx]
    // Hack because received lesson object is different structure from original, so only take prop
    updatedLesson[updateObj.noteType] = updateObj.payload[updateObj.noteType]
    Vue.set(state.events, idx, updatedLesson)
  },
  setActiveCalendar (state, calendar) {
    state.calendar = calendar
    state.calendarId = calendar.id
  },
  setActiveCalendarEventId (state, eventId) {
    state.activeEventId = eventId
  },
  setCmdKeyDown (state, bool) {
    state.isCmdKeyDown = bool
  },
  toggleSelectedEventId (state, eventId) {
    const idx = state.selectedEventIds.indexOf(eventId)
    if (idx > -1) {
      state.selectedEventIds.splice(idx, 1)
    } else {
      state.selectedEventIds.push(eventId)
    }
  },
  setSelectedEventIds (state, ids) {
    state.selectedEventIds = ids
  },
  clearSelectedEventIds (state) {
    state.selectedEventIds = []
  }
}

const actions = {
  readCalendar (context, calendarId) {
    docendo.calendar(calendarId, data => {
      context.commit('setActiveCalendar', data)
    }, failure => {
      console.log('failure', failure)
    })
  },
  readCalendarSubjects (context, isNewCalendar) {
    if (context.state.isSubjectsLoadingTimer) {
      clearTimeout(context.state.isSubjectsLoadingTimer)
    }
    if (!isNewCalendar) {
      context.state.isSubjectsLoadingTimer = setTimeout(_ => {
        context.state.isSubjectsLoading = true
      }, 2000)
    } else {
      context.state.subjects = []
      context.state.isSubjectsLoading = true
    }
    if (!context.state.calendarId) {
      return
    }
    docendo.sidebar(context.state.calendarId, context.state.date, success => {
      clearTimeout(context.state.isSubjectsLoadingTimer)
      context.state.isSubjectsLoading = false
      context.state.subjects = success
    }, failure => {
      console.log('something went wrong')
    })
  },
  createCalendarSubject: (context, subject) => {
    console.log('vuex1::createCalendarSubject', subject)

    function checkIfActiveCalendarDateIsSameOrInBetweenSchoolYear (schoolYearId) {
      const schoolYear = context.rootState.schoolYears.find(schoolYear => schoolYear.id === schoolYearId)
      const schoolCalendarType = context.rootGetters.allCalendarsInHash[subject.calendarId] ? context.rootGetters.allCalendarsInHash[subject.calendarId]['type'] : null
      if (!schoolYear || !schoolCalendarType) {
        return true
      }
      let startDate = moment(schoolYear.employee_time_start)
      let endDate = moment(schoolYear.employee_time_end)
      if (schoolCalendarType === 'klass') {
        startDate = moment(schoolYear.student_time_start)
        endDate = moment(schoolYear.student_time_end)
      }
      const activeDate = context.state.date
      const isBetweenOrSame = moment(activeDate).isSameOrBefore(endDate) && moment(activeDate).isSameOrAfter(startDate)
      return isBetweenOrSame
    }

    const schoolId = context.rootState.activeSchool.id
    const preparation = subject.preparation != null && subject.preparation !== '' ? subject.preparation : null
    const away = subject.away ? subject.away : false
    docendo.createSubject(subject.name, subject.color, subject.calendarId, subject.defaultDuration, preparation, subject.hours, subject.subjectType, schoolId, subject.schoolYearId, subject.calendarIds, subject.subjectCode, away, null, null, subject.taskOverview, data => {
      if (checkIfActiveCalendarDateIsSameOrInBetweenSchoolYear(data.school_year_id)) {
        context.commit('addCalendarSubject', data)
      } else {
        const explainerI18n = window.use_i18n.t('vue2.create_subject.subject_is_not_created_in_this_school_year')
        eventbus.toast = {text: explainerI18n, success: false}
      }
    }, failure => {
      console.log('failure')
    })
  },
  deleteCalendarSubject: (context, subject) => {
    docendo.deleteSubject(subject.id, subject.school_id, subject.school_year_id, data => {
      context.dispatch('readCalendarSubjects')
    }, failure => {
      console.log('failure updated Topics')
    })
  },
  updateCalendarSubject: (context, updatedSubject) => {
    console.log('vuex1::updatedSubject', updatedSubject)
    const preparation = updatedSubject.preparation != null && updatedSubject.preparation !== '' ? updatedSubject.preparation : null
    const away = updatedSubject.away ? updatedSubject.away : true
    docendo.updateSubject(updatedSubject.id, updatedSubject.name, updatedSubject.color, updatedSubject.calendarId, updatedSubject.calendarIds, updatedSubject.defaultDuration, preparation, updatedSubject.hours, updatedSubject.subjectType, updatedSubject.schoolId, updatedSubject.schoolYearId, updatedSubject.subjectCode, away, false, null, null, updatedSubject.taskOverview, data => {
      context.dispatch('readCalendarSubjects')
    }, failure => {
      console.log('failure')
    })
  },
  readCalendarEvents (context, obj) {
    context.state.isEventsLoading = true
    docendo.calendarEvents2(obj.calIds, obj.start, obj.end, null,
      data => {
        context.state.isEventsLoading = false
        clearTimeout(context.state.isEventsLoadingTimer)
        context.state.events = data.length > 0 ? JSON.parse(data) : []
        if (obj.andReadSubjects) {
          context.dispatch('readCalendarSubjects')
        }
      }, failure => {
        console.log('failure', failure)
      })
  },
  createCalendarEvent (context, eventObj) {
    const tempEventId = new Date().getTime()
    Vue.set(eventObj, 'event_id', tempEventId)
    let calendarIds = []
    if (eventObj.calendars && Array.isArray(eventObj.calendars)) {
      eventObj.calendars.filter(calendar => calendar && calendar.calendar_type && calendar.calendar_type !== 'school').forEach(calendar => calendarIds.push(calendar.id))
    }
    if (calendarIds.indexOf(eventObj.calendar_id) < 0) {
      calendarIds.push(eventObj.calendar_id)
    }
    context.commit('addActiveCalendarEvent', eventObj)
    docendo.createEvent(eventObj.school_id, eventObj.calendar_id, calendarIds, eventObj.subject_id, eventObj.start, eventObj.end, eventObj.learning_goal, eventObj.description, eventObj.notes_for_students, eventObj.teacher_notes, success => {
      if (success && success.status === 'error') {
        eventbus.toast = {text: 'Something went wrong', 'success': false}
      }
      const calendarInfo = {
        calIds: [context.state.calendarId],
        start: context.getters.calendarStartDate,
        end: context.getters.calendarEndDate
      }
      context.dispatch('readCalendarEvents', calendarInfo)
      context.dispatch('readCalendarSubjects')
    }, failure => {
      eventbus.toast = {text: 'Something went wrong', 'success': false}
    })
    console.log('createEVentVuex', eventObj)
  },
  createCalendarEvents (context, eventArr) {
    eventArr.forEach((eventObj, key) => {
      const tempEventId = new Date().getTime() + eventObj.event_id
      Vue.set(eventObj, 'event_id', tempEventId)
      let calendarIds = []
      eventObj.calendars.forEach(calendar => calendarIds.push(calendar.calendar_id))
      if (calendarIds.indexOf(eventObj.calendar_id) < 0) {
        calendarIds.push(eventObj.calendar_id)
      }
      context.commit('addActiveCalendarEvent', eventObj)
      docendo.createEvent(eventObj.school_id, eventObj.calendar_id, calendarIds, eventObj.subject_id, eventObj.start, eventObj.end, eventObj.learning_goal, eventObj.description, eventObj.notes_for_students, eventObj.teacher_notes, success => {
        if (success && success.status === 'error') {
          eventbus.toast = {text: 'Something went wrong', 'success': false}
        }
        const allDone = key + 1 === eventArr.length
        if (allDone) {
          const calendarInfo = {
            calIds: [context.state.calendarId],
            start: context.getters.calendarStartDate,
            end: context.getters.calendarEndDate
          }
          context.dispatch('readCalendarEvents', calendarInfo)
          context.dispatch('readCalendarSubjects')
        }
      }, failure => {
        eventbus.toast = {text: 'Something went wrong', 'success': false}
      })
    })
  },
  updateCalendarEventNotes (context, updatedLesson) {
    // Different permission "can_edit_own_notes"
    docendo.updateEvent(updatedLesson.schoolId, updatedLesson.eventId, updatedLesson.updates, success => {
      if (success && success.status === 'error') {
        console.log('failure', success)
        eventbus.toast = {text: 'Something went wrong', 'success': false}
      }
      const calendarInfo = {
        calIds: [context.state.calendarId],
        start: context.getters.calendarStartDate,
        end: context.getters.calendarEndDate
      }
      context.dispatch('readCalendarEvents', calendarInfo)
    }, failure => {
      eventbus.toast = {text: 'Something went wrong', 'success': false}
    })
  },
  updateCalendarEvent (context, updatedLesson) {
    context.commit('updateCalendarEvents', updatedLesson)
    // updatedLesson = {school_id: 3, updates: [{event_id: 1, calendar_id: 1, start_time: '2021-01-01 12:00Z', end_time: '2021-01-01 13:00Z'}]}
    docendo.updateEvents(updatedLesson.school_id, updatedLesson.updates, success => {
      if (success && success.status === 'error') {
        console.log('failure', success)
        eventbus.toast = {text: 'Something went wrong', 'success': false}
      }
      const calendarInfo = {
        calIds: [context.state.calendarId],
        start: context.getters.calendarStartDate,
        end: context.getters.calendarEndDate
      }
      context.dispatch('readCalendarEvents', calendarInfo)
      context.dispatch('readCalendarSubjects')
    }, failure => {
      eventbus.toast = {text: 'Something went wrong', 'success': false}
    })
  },
  deleteEvent (context, lesson) {
    context.state.isDeletingEvent = true
    docendo.deleteEvent(lesson.school_id, lesson.event_id, success => {
      context.state.isDeletingEvent = false
      context.commit('setActiveCalendarEventId', null)
      context.commit('deleteActiveCalendarEvent', lesson)
      context.dispatch('readCalendarSubjects')
      if (Vue.prototype.$helpers.getLocalStorage('do_dont_ask_delete_event')) {
        const title = lesson.title ? lesson.title : 'The event'
        const capitalize = s => (s && s[0].toUpperCase() + s.slice(1)) || ''
        const lessonTime = lesson.start && lesson.end ? capitalize(moment(lesson.start).format('dddd LT')) + ' - ' + moment(lesson.end).format('LT') : ''
        const isDeleted = window.use_i18n.t('vue2.common.is_deleted')
        eventbus.toast = {text: title + ' ' + isDeleted + ' ' + lessonTime, success: true}
      }
    }, failure => {
      eventbus.toast = {text: 'Something went wrong', 'success': false}
    })
  },
  readEventAvailability (context, lesson) {
    docendo.eventAvailability(lesson.calendarId, lesson.eventId, context.getters.calendarStartDate, context.getters.calendarEndDate, success => {
      context.state.availabilityEvents = success.length > 0 ? JSON.parse(success) : []
    }, failure => {
      eventbus.toast = {text: 'Something went wrong', 'success': false}
    })
  },
  readSubjectAvailability (context, subject) {
    docendo.subjectAvailability(subject.calendarId, subject.subjectId, context.getters.calendarStartDate, context.getters.calendarEndDate, success => {
      context.state.availabilityEvents = success.length > 0 ? JSON.parse(success) : []
    }, failure => {
      eventbus.toast = {text: 'Something went wrong', 'success': false}
    })
  },
  clearAvailability (context) {
    context.state.availabilityEvents = []
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}



// WEBPACK FOOTER //
// ./src/store/modules/calendar.js